import { memo, useRef, useState } from 'react';

import { AnalyticsCategory, RoutePath } from '@common/constants';
import { Redirect } from '@core/navigation';
import { useTranslation } from '@wearehelpful/ui-localization';
import {
  Content,
  Typography,
  Form,
  Footer,
  FormInstance,
} from '@wearehelpful/ui-kit';
import { useBootstrapData } from '@bootstrap/hooks';
import { BDTO, ContentfulDocument, useGetCounties } from '@helpful/network';
import { TrackPageView } from '@wearehelpful/ui-stats';

import { ZipCodeForm } from './ZipCode.types';

export const ZipCode = memo(() => {
  const { t } = useTranslation();
  const form = useRef<FormInstance>();
  const [filter, setFilter] = useState<ZipCodeForm>();

  const { hints } = useBootstrapData();

  const { isFetching, data } = useGetCounties(
    {
      filter,
    },
    {
      query: {
        enabled: !!filter,
        onSuccess: ({ data }) => {
          if (!data.length) {
            form.current.setErrors({
              zip: t('validation.pattern', {
                label: t('forms.zipCode'),
              }),
            });
          }
        },
      },
    }
  );

  const hint = hints.find(
    ({ type }) =>
      type === BDTO.HintItemType['why-do-we-need-this-info-zip-code']
  );

  const handleOnSubmit = (form: ZipCodeForm) => setFilter(() => form);

  if (data?.data.length && data?.data.length === 1) {
    return (
      <Redirect
        path={RoutePath.AUTH_SIGN_UP_INSURANCE}
        search={{ ...filter, fips: data?.data[0].fips }}
      />
    );
  }

  if (data?.data.length && data?.data.length > 1) {
    return <Redirect path={RoutePath.AUTH_SIGN_UP_COUNTY} search={filter} />;
  }

  return (
    <TrackPageView
      category={AnalyticsCategory.AUTH}
      name={AnalyticsCategory.ZIP_CODE}
    >
      <Form<ZipCodeForm> ref={form} onSubmit={handleOnSubmit}>
        <Content>
          <Content.Progress value={1 / 5} />
          <Content.Subheader>
            <Typography variant="h2">
              {t('subheadings.signUpZip.title')}
            </Typography>
          </Content.Subheader>
          <Content.Fixed>
            <Form.Section description={t('forms.zipCodeInformation')}>
              <Form.Text
                autofocus
                tabIndex={1}
                name="zip"
                validation={{
                  required: true,
                  pattern: /^\d{5}(-\d{4})?$/,
                }}
                label={t('forms.zipCode')}
              />
              {!!hint && (
                <Form.Hint
                  title={hint.title}
                  message={
                    <ContentfulDocument>
                      {hint.longDescription as any}
                    </ContentfulDocument>
                  }
                >
                  {hint.shortDescription}
                </Form.Hint>
              )}
            </Form.Section>
          </Content.Fixed>
        </Content>
        <Footer>
          <Form.Submit checkValidity loading={isFetching}>
            {t('actions.next')}
          </Form.Submit>
        </Footer>
      </Form>
    </TrackPageView>
  );
});
